import React, { useEffect, useRef, useState } from 'react';
import { Seo } from './components/Seo';
import { MetaPixel } from './components/MetaPixel';
import { BundleMockup, BonusCover } from './components/BundleMockup';
import { scrollToOffer, goToCheckout } from './lib/checkout';
import { PRICE, ORIGINAL_PRICE, BONUS_IMAGES, SUPPORT_EMAIL, INSTAGRAM_HANDLE, INSTAGRAM_URL } from './data/config';
import { VOLUMES, PAINS, INSIDE, PREVIEW, BONUSES, STEPS, FAQ, Volume } from './data/content';
const BONUS_TOTAL = BONUSES.reduce((s, b) => s + b.value, 0);
const TOTAL_VALUE = ORIGINAL_PRICE + BONUS_TOTAL;
function Label({
  children,
  light = false
}: {
  children: React.ReactNode;
  light?: boolean;
}) {
  return <p className={`text-[11px] font-bold uppercase tracking-[0.22em] ${light ? 'text-terra' : 'text-sage'}`} data-id="element-0" data-mp="2wka20ve6b74d">{children}</p>;
}

// Scroll-only button (never goes to checkout).
function SeeButton({
  text
}: {
  text: string;
}) {
  return <button type="button" onClick={scrollToOffer} className="w-full rounded-full bg-terra px-5 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_6px_0_#b85a42] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_3px_0_#b85a42] sm:text-xl" data-id="element-1" data-mp="23u9q4cl98ts0">
      {text} ↓
    </button>;
}
function VolumeItem({
  v,
  open,
  onToggle
}: {
  v: Volume;
  open: boolean;
  onToggle: () => void;
}) {
  const start = parseInt(v.range.replace(/[^0-9–]/g, '').split('–')[0], 10);
  return <div className="card overflow-hidden" data-id="element-2" data-mp="2zmwlj96ys6hj">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 p-4 text-left" aria-expanded={open} data-id="element-3" data-mp="33m5a7zn5de68">
        <span className="font-display flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sage text-xl font-extrabold text-cream" data-id="element-4" data-mp="38l03xwmnkn54">{v.num}</span>
        <span className="min-w-0 flex-1" data-id="element-5" data-mp="nvsvva68mxyl">
          <span className="block text-[10px] font-bold tracking-[0.15em] text-muted" data-id="element-6" data-mp="nur948fv0n11">VOLUME {v.num} · {v.range.toUpperCase()}</span>
          <span className="font-display block text-lg font-bold leading-tight sm:text-xl" data-id="element-7" data-mp="swqbbezv5dwe">{v.title}</span>
          <span className="block text-xs italic text-ink/70" data-id="element-8" data-mp="38b6yx8f1wei">“{v.pack}”</span>
        </span>
        <span className={`shrink-0 text-2xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-9" data-mp="3uevqzx020421">+</span>
      </button>
      {open && <div className="border-t border-line bg-creamy/60 p-4" data-id="element-10" data-mp="86mev2f5hwu3">
          <p className="text-sm text-ink/85" data-id="element-11" data-mp="3gleakl2nz7g8">{v.summary}</p>
          <ol className="mt-3 grid gap-x-4 gap-y-1.5 sm:grid-cols-2" data-id="element-12" data-mp="3glnze5bz38kc">
            {v.sessions.map((s, i) => <li key={s} className="flex min-w-0 gap-2 text-sm" data-id="element-13" data-mp="3bk718hkvi07g" data-mpi={i}>
                <span className="shrink-0 text-[12px] font-bold text-terra" data-id="element-14" data-mp="3qbzgktko3e96">{String(start + i).padStart(2, '0')}</span>
                <span className="min-w-0 break-words" data-id="element-15" data-mp="3lj6aa19jvxuz">{s}</span>
              </li>)}
          </ol>
          <p className="mt-3 text-xs font-semibold text-sage" data-id="element-16" data-mp="2xssgo6bbhqya">+ 13 matching client worksheets</p>
        </div>}
    </div>;
}
function FaqItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-line" data-id="element-17" data-mp="1d4zuqxigxatm">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start justify-between gap-3 py-4 text-left" aria-expanded={open} data-id="element-18" data-mp="1d5sq72bz8dwg">
        <span className="font-semibold" data-id="element-19" data-mp="13e52ncijjh09">{q}</span>
        <span className={`shrink-0 text-xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-20" data-mp="13ey7g7xxvwfr">+</span>
      </button>
      {open && <p className="pb-4 text-sm leading-relaxed text-ink/85" data-id="element-21" data-mp="1w3vknwnan31n">{a}</p>}
    </div>;
}

// Mobile-only sticky bar: scrolls to #offer, hidden near the top and while the offer is visible.
function StickyBar() {
  const [show, setShow] = useState(false);
  const offerVisible = useRef(false);
  const scrolled = useRef(false);
  useEffect(() => {
    const update = () => setShow(scrolled.current && !offerVisible.current);
    const onScroll = () => {
      scrolled.current = window.scrollY > 520;
      update();
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    const el = document.getElementById('offer');
    let io: IntersectionObserver | null = null;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(entries => {
        offerVisible.current = entries[0].isIntersecting;
        update();
      }, {
        threshold: 0.05
      });
      io.observe(el);
    }
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      io?.disconnect();
    };
  }, []);
  return <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-3 py-2 backdrop-blur transition-transform duration-300 md:hidden ${show ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-22" data-mp="zxk557qreliq">
      <div className="flex items-center gap-3" data-id="element-23" data-mp="3knsx8o6647j4">
        <div className="min-w-0 flex-1 leading-tight" data-id="element-24" data-mp="3kpm5u90cd1tr">
          <p className="truncate text-sm font-extrabold uppercase" data-id="element-25" data-mp="3glxzftng4ca3">78 Sessions + 78 Worksheets</p>
          <p className="text-xs" data-id="element-26" data-mp="1w4yk20vebtzi">
            <span className="text-muted line-through" data-id="element-27" data-mp="1w5zxgbzvwske">${ORIGINAL_PRICE}</span> <span className="font-bold text-terra" data-id="element-28" data-mp="205iid7rph3n8">${PRICE}</span>
          </p>
        </div>
        <button type="button" onClick={scrollToOffer} className="shrink-0 rounded-full bg-terra px-4 py-2 text-sm font-bold uppercase text-white" data-id="element-29" data-mp="254lc3w28jnfw">
          See offer ↓
        </button>
      </div>
    </div>;
}
export function App() {
  const [openVol, setOpenVol] = useState<number>(0);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#fffdf9]" data-id="element-30" data-mp="2jthsqhicy8s4">
      <Seo data-id="element-31" data-mp="2jvu60hd67uyu" />
      <MetaPixel data-id="element-32" data-mp="2nvun69rnmk9l" />

      <header className="border-b border-line bg-white" data-id="element-33" data-mp="2nvcshhkn4e3s">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3" data-id="element-34" data-mp="2ntrzhwdz5381">
          <p className="font-display text-lg font-extrabold tracking-wide text-sage" data-id="element-35" data-mp="2sslani5sgi51">
            SIGMA SMART<span className="text-terra" data-id="element-36" data-mp="tjodxgibmdjx">™</span>
          </p>
          <p className="truncate text-sm font-extrabold uppercase tracking-wide text-terra" data-id="element-37" data-mp="yhg1xzfuhfiu">Coaches</p>
        </div>
      </header>

      {/* 1. HERO */}
      <section className="bg-sagegrad text-white" data-id="element-38" data-mp="1wshb64wiu01o">
        <div className="mx-auto grid max-w-5xl items-center gap-8 px-4 py-10 md:grid-cols-2 md:py-16" data-id="element-39" data-mp="1wtsmqqw7kc3c">
          <div className="min-w-0" data-id="element-40" data-mp="2attqqf7rrzn1">
            <span className="pill bg-white/15 text-cream" data-id="element-41" data-mp="25t5oexe3bwdi">FOR LIFE, CAREER, EXECUTIVE & WELLNESS COACHES</span>
            <h1 className="font-display mt-4 text-[40px] font-extrabold leading-[1.02] sm:text-6xl" data-id="element-42" data-mp="2kq6hb480pxdk">
              Stop planning sessions from scratch.
              <br data-id="element-43" data-mp="2fqtu2id8ioag" />
              <span className="text-terra" data-id="element-44" data-mp="2fp912x6kjdep">Start coaching.</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/90 sm:text-lg" data-id="element-45" data-mp="2ti8camzbilgg">
              <strong className="text-white" data-id="element-46" data-mp="2pig1s4qd658g">78 ready-to-run coaching sessions</strong>, each with its own <strong className="text-white" data-id="element-47" data-mp="33hz8ct3j18in">client worksheet</strong>: goals, mindset, stress,
              habits, boundaries, leadership and career. Open it, pick a session, and coach.
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-white/90" data-id="element-48" data-mp="3i4y1csi18azh">
              <li data-id="element-49" data-mp="3n39b738bulhp">✔ 6 volumes · 156 sessions & worksheets</li>
              <li data-id="element-50" data-mp="3n0n1oy1vlvwu">✔ For 1:1, groups and online sessions</li>
              <li data-id="element-51" data-mp="384fahds1vu82">✔ + 4 bonuses included free</li>
            </ul>
          </div>
          <div className="min-w-0" data-id="element-52" data-mp="3d40gu99jv4ut">
            <BundleMockup data-id="element-53" data-mp="3d2ywfe6n960f" />
            <div className="mt-4" data-id="element-54" data-mp="3d6d14t2s5rb1">
              <SeeButton text="See the 78 sessions now" data-id="element-55" data-mp="2zcvev0ipm0hb" />
            </div>
            <p className="mt-3 text-center text-[12px] text-white/75" data-id="element-56" data-mp="343vewen4kyo1">Instant PDF access · 7-day risk-free guarantee</p>
          </div>
        </div>
      </section>

      {/* 2. SOUND FAMILIAR */}
      <section className="bg-creamy" data-id="element-57" data-mp="2ue21r97c4bho">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-58" data-mp="2uf3my6i2eed4">
          <Label data-id="element-59" data-mp="2ge9czjfdc5cc">Sound familiar?</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-60" data-mp="2gfc33p4va3r7">You love coaching. Not rebuilding sessions every week.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-61" data-mp="2khp82bbshxmx">
            {PAINS.map(p => <div key={p.title} className="card min-w-0 p-5" data-id="element-62" data-mp="2vwaorgt9c79t">
                <span className="pill bg-sage/10 text-sage" data-id="element-63" data-mp="2vx2a7yt54omd">{p.tag}</span>
                <h3 className="font-display mt-3 text-xl font-bold leading-tight" data-id="element-64" data-mp="2qw68nrw9hek4">{p.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-65" data-mp="2lx31o1wq0y9i">{p.text}</p>
              </div>)}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-lg" data-id="element-66" data-mp="2hxayk4q3jkbj">
            You already have the skills. This gives you the sessions, written and ready, so your prep time goes back to your clients and your life.
          </p>
        </div>
      </section>

      {/* 3. VOLUMES */}
      <section className="bg-white" data-id="element-67" data-mp="238o9vcekxg7n">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-68" data-mp="235imq2pigoek">
          <Label data-id="element-69" data-mp="1y7h2hegcmj1i">6 volumes · 78 sessions · 78 worksheets</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-70" data-mp="1u6x467k1h9ds">A session ready for whatever your client brings.</h2>
          <p className="mt-3 text-ink/80" data-id="element-71" data-mp="9o4kh64umiyd">Open each volume to see all 13 session titles.</p>
          <div className="mt-8 space-y-4" data-id="element-72" data-mp="dpgdzmqufobu">
            {VOLUMES.map((v, i) => <VolumeItem key={v.num} v={v} open={openVol === i} onToggle={() => setOpenVol(openVol === i ? -1 : i)} data-id="element-73" data-mp="ipb2jjn5c1tq" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* 4. INSIDE EVERY SESSION + PREVIEW */}
      <section className="bg-sagegrad text-white" data-id="element-74" data-mp="3m7m5fusim9wg">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-75" data-mp="3r54ju0paxhbu">
          <Label light data-id="element-76" data-mp="3r5xfa5it8keo">Inside every session</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-77" data-mp="3w4qtk05x8ffw">Everything you need to run it, on one page.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-78" data-mp="4re4sjwyhpdh">
            {INSIDE.map((p, i) => <div key={p.title} className="min-w-0 rounded-2xl border border-white/20 bg-white/10 p-5" data-id="element-79" data-mp="33fjpklnp6xn9" data-mpi={i}>
                <p className="text-[11px] font-bold text-terra" data-id="element-80" data-mp="38ctlmk5le7k2">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-display mt-1 text-xl font-bold" data-id="element-81" data-mp="2f97ul5tkn8c0">{p.title}</h3>
                <p className="mt-1 text-sm text-white/85" data-id="element-82" data-mp="2k4n81wno327w">{p.text}</p>
              </div>)}
          </div>

          <div className="mt-10 min-w-0 rounded-2xl bg-white p-5 text-ink sm:p-7" data-id="element-83" data-mp="2p3873sr6nfur">
            <span className="pill bg-terra/15 text-terra" data-id="element-84" data-mp="2u3mi9jo2z5x0">REAL SESSION PREVIEW</span>
            <h3 className="font-display mt-3 text-2xl font-extrabold leading-tight text-sage" data-id="element-85" data-mp="1whyoz7mx93pp">{PREVIEW.title}</h3>
            <p className="mt-3 text-sm" data-id="element-86" data-mp="21g05bksyqoxh"><strong data-id="element-87" data-mp="26etjlfg2qjyp">Goal:</strong> {PREVIEW.goal}</p>
            <p className="mt-1 text-sm" data-id="element-88" data-mp="26e1nm3ikek7w"><strong data-id="element-89" data-mp="26cqio0ddv6f7">Best for:</strong> {PREVIEW.bestFor}</p>
            <p className="mt-1 text-sm text-muted" data-id="element-90" data-mp="2b6lajr356rpd">{PREVIEW.time}</p>
            <p className="mt-4 text-sm font-bold" data-id="element-91" data-mp="1dqe4kswy01nd">Core exercise</p>
            <ol className="mt-2 space-y-1.5" data-id="element-92" data-mp="1do1rou61khh6">
              {PREVIEW.steps.map((s, i) => <li key={s} className="flex min-w-0 gap-2 text-sm" data-id="element-93" data-mp="1inefbh4qlskt" data-mpi={i}>
                  <span className="shrink-0 font-bold text-terra" data-id="element-94" data-mp="3vyh65smw754k">{i + 1}.</span>
                  <span className="min-w-0 break-words" data-id="element-95" data-mp="3rxdnkydgmuzn">{s}</span>
                </li>)}
            </ol>
            <p className="mt-4 text-sm font-bold" data-id="element-96" data-mp="3mxr9yntpzcqb">Powerful questions</p>
            <ul className="mt-2 space-y-1" data-id="element-97" data-mp="3i091sznj11jd">
              {PREVIEW.questions.map(q => <li key={q} className="text-sm italic text-ink/85" data-id="element-98" data-mp="3d0eboybksfzd">• {q}</li>)}
            </ul>
            <p className="mt-4 rounded-xl bg-creamy p-3 text-xs text-ink/80" data-id="element-99" data-mp="389efjvaa61xx">…plus the group and online version, the between-session action, the closing question, a coach note and Worksheet 02 for your client.</p>
          </div>
        </div>
      </section>

      {/* 5. BONUSES */}
      <section className="bg-creamy" data-id="element-100" data-mp="2ua58bl41yi63">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-101" data-mp="2u93n4ntbofan">
          <Label data-id="element-102" data-mp="2pb23o1rzidyn">Included free · 4 bonuses</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-103" data-mp="2pcd5vx5s7dnn">Plus 4 bonuses for your coaching practice.</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-104" data-mp="2fpsrjyndujdt">
            {BONUSES.map((b, i) => <div key={b.title} className="card flex min-w-0 gap-4 p-4" data-id="element-105" data-mp="2kk6hvmn6u0bm" data-mpi={i}>
                <div className="w-[38%] max-w-[150px] shrink-0" data-id="element-106" data-mp="26okv7m4ffsmd">
                  <BonusCover index={i} image={BONUS_IMAGES[i] || ''} short={b.short} data-id="element-107" data-mp="2anld92f2yr8b" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-108" data-mp="2yez5z9h58ott">
                  <p className="text-[11px] font-bold text-terra" data-id="element-109" data-mp="33d0x8hoq3ugd">BONUS {i + 1}</p>
                  <h3 className="font-display text-lg font-bold leading-tight sm:text-xl" data-id="element-110" data-mp="33f4x8oji49bd">{b.title}</h3>
                  <p className="mt-1 text-sm text-ink/80" data-id="element-111" data-mp="2pjjayp4nk3mn">{b.text}</p>
                  <p className="mt-2 text-sm" data-id="element-112" data-mp="2tlo4zoz0j29t">
                    <span className="text-muted line-through" data-id="element-113" data-mp="3hcthbu5qvcfd">${b.value}</span> <span className="font-bold text-go" data-id="element-114" data-mp="3hb8me3fiprgz">FREE</span>
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section className="bg-white" data-id="element-115" data-mp="1amgfj7wwvha1">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-116" data-mp="15pzuwnox7l1p">
          <Label data-id="element-117" data-mp="15ndmymy4b3iy">How it works</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-118" data-mp="1kevya7g189vz">Open it. Pick a session. Coach.</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-119" data-mp="1fgko1vltrxd8">
            {STEPS.map(s => <div key={s.n} className="card min-w-0 p-5" data-id="element-120" data-mp="rqqb7e1ia8sz">
                <span className="font-display text-5xl font-extrabold leading-none text-terra" data-id="element-121" data-mp="mqlw9sg8xm4v">{s.n}</span>
                <h3 className="font-display mt-3 text-xl font-bold" data-id="element-122" data-mp="11nlba1dfgh9d">{s.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-123" data-mp="11licaruardsi">{s.text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. OFFER — the ONLY checkout button lives here */}
      <section id="offer" className="bg-sagegrad scroll-mt-4 text-white" data-id="element-124" data-mp="283xcycfgkh9p">
        <div className="mx-auto max-w-xl px-4 py-14" data-id="element-125" data-mp="283ngq73tldvk">
          <div className="text-center" data-id="element-126" data-mp="hnlcau375c4k">
            <Label light data-id="element-127" data-mp="honzos1b8wfq">The offer</Label>
            <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-128" data-mp="mlefrfd47s15">Everything you get</h2>
          </div>
          <div className="mt-6 overflow-hidden rounded-2xl bg-white text-ink shadow-2xl" data-id="element-129" data-mp="qmqa1y6xp1fo">
            <div className="p-3" data-id="element-130" data-mp="vnnmrv96d39s">
              <BundleMockup compact data-id="element-131" data-mp="vkhygi8de5f4" />
            </div>
            <div className="p-4 pt-0 sm:p-6 sm:pt-0" data-id="element-132" data-mp="10mh11pwacyb0">
              <ul className="divide-y divide-line text-sm" data-id="element-133" data-mp="10juvtwwvauvy">
                <li className="flex items-center justify-between gap-3 py-3" data-id="element-134" data-mp="15jh926cp4b4r">
                  <span className="min-w-0 font-semibold" data-id="element-135" data-mp="1ab8rtupm1ckc">📘 150+ Done-For-You Coaching Sessions & Worksheets™ · 6 Volumes</span>
                  <span className="shrink-0 text-muted line-through" data-id="element-136" data-mp="1eckmienccnze">${ORIGINAL_PRICE}</span>
                </li>
                {BONUSES.map(b => <li key={b.title} className="flex items-center justify-between gap-3 py-3" data-id="element-137" data-mp="1ob0a47n5tf45">
                    <span className="flex min-w-0 items-start gap-2" data-id="element-138" data-mp="1yexadqygojx3">
                      <span className="shrink-0 text-base leading-none" data-id="element-139" data-mp="1yd4081on3hkc">🎁</span>
                      <span className="min-w-0" data-id="element-140" data-mp="1tej1y7sy77yj">{b.title}</span>
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-right" data-id="element-141" data-mp="1ohj4gla5zdj1">
                      <span className="text-muted line-through" data-id="element-142" data-mp="1kg77tvsvhs1c">${b.value}</span> <span className="font-bold text-go" data-id="element-143" data-mp="1khs2rmj3nczq">FREE</span>
                    </span>
                  </li>)}
                <li className="flex items-center gap-2 py-3" data-id="element-144" data-mp="2c6bszwogij3m">
                  <span className="shrink-0 font-bold text-go" data-id="element-145" data-mp="2c8y1byj692mw">✔</span>
                  <span className="font-semibold" data-id="element-146" data-mp="27e26kqmjw163">7-Day Risk-Free Guarantee</span>
                </li>
                <li className="flex items-center gap-2 py-3" data-id="element-147" data-mp="23e1i0d5qm9lb">
                  <span className="shrink-0 font-bold text-go" data-id="element-148" data-mp="2zwfy68hamqwq">✔</span>
                  <span className="font-semibold" data-id="element-149" data-mp="2v5pqgoleabyv">Lifetime Access &amp; Free Updates</span>
                </li>
              </ul>
              <div className="mt-4 border-t border-line pt-4 text-center" data-id="element-150" data-mp="t8ypmx9d4w9i">
                <p className="text-sm" data-id="element-151" data-mp="y6qsgmbjq2sh">
                  Total value: <span className="text-muted line-through" data-id="element-152" data-mp="y68muz2zuc7w">${TOTAL_VALUE}</span>
                </p>
                <p className="mt-1 text-sm" data-id="element-153" data-mp="jhubfzrihffq">
                  Regular price <span className="line-through" data-id="element-154" data-mp="ogfavwyxvv34">${ORIGINAL_PRICE}</span> · your price, one-time payment:
                </p>
                <p className="font-display text-7xl font-extrabold leading-none text-sage" data-id="element-155" data-mp="agmm476z3oxs">${PRICE}</p>
                <p className="mt-3 text-xs text-muted" data-id="element-156" data-mp="fi3pqagudpi2">· Payment will be processed in your local currency ·</p>
                <button type="button" onClick={goToCheckout} className="mt-3 w-full rounded-full bg-go px-4 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_5px_0_#248a16] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_2px_0_#248a16] sm:text-xl" data-id="element-157" data-mp="l2tpurmb8ds">
                  Get the 78 sessions now
                </button>
                <p className="mt-3 text-[11px] text-muted" data-id="element-158" data-mp="3ffletknuqfkr">Secure checkout · Instant PDF access</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. GUARANTEE */}
      <section className="bg-creamy" data-id="element-159" data-mp="3pdrctr6jf1kc">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-160" data-mp="3pcpur2r3tet4">
          <div className="card flex flex-col items-center gap-5 p-6 text-center sm:flex-row sm:text-left" data-id="element-161" data-mp="6z8gdp3vefpo">
            <div className="font-display flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-sage text-3xl font-extrabold text-cream" data-id="element-162" data-mp="2yg7iwywuctx">7</div>
            <div className="min-w-0" data-id="element-163" data-mp="gvlj1che83wk">
              <h2 className="font-display text-3xl font-extrabold leading-tight" data-id="element-164" data-mp="by39pkzgrmo1">7-Day Risk-Free Guarantee</h2>
              <p className="mt-2 text-ink/85" data-id="element-165" data-mp="2io9gf57cvx4f">
                Open the volumes and run a session with your next client. If the toolkit does not fit your coaching, email us within 7 days of purchase and you get your
                money back.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-white" data-id="element-166" data-mp="1wtsnkhkf297j">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-167" data-mp="21mvgcnhddemo">
          <Label data-id="element-168" data-mp="21ndmqcxqx98b">Questions</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-169" data-mp="26lf7r3eqfimf">Frequently asked questions</h2>
          <div className="mt-6 border-t border-line" data-id="element-170" data-mp="18yq0ae19gdt6">
            {FAQ.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-171" data-mp="1dwhnwvuvhdrk" />)}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA (scrolls to the offer) */}
      <section className="bg-sagegrad text-white" data-id="element-172" data-mp="1iy740pwychmi">
        <div className="mx-auto max-w-2xl px-4 py-14 text-center" data-id="element-173" data-mp="1nvgyijz77jh7">
          <h2 className="font-display text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-174" data-mp="2ub9i9ebkwv3i">
            Less time planning.
            <br data-id="element-175" data-mp="2z9ksvr9p79ms" />
            <span className="text-terra" data-id="element-176" data-mp="2z9axtp9sqca8">More time coaching.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-white/85" data-id="element-177" data-mp="nna8st61jy21">78 sessions, 78 worksheets and 4 bonuses, ready for your next client, group or workshop.</p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-178" data-mp="iq0lpk64jyhd">
            <SeeButton text="See the 78 sessions now" data-id="element-179" data-mp="dwfkzkd5cqeh" />
          </div>
        </div>
      </section>

      {/* 11. FOOTER */}
      <footer className="border-t border-line bg-white pb-24 md:pb-10" data-id="element-180" data-mp="8xl4ip2njdxj">
        <div className="mx-auto max-w-3xl px-4 pt-10 text-center" data-id="element-181" data-mp="3z9t49wpkvd7">
          <p className="font-display text-2xl font-extrabold tracking-wide text-sage" data-id="element-182" data-mp="3w2tyji7i6ycc">
            SIGMA SMART<span className="text-terra" data-id="element-183" data-mp="3r3ps2zbyrepp">™</span>
          </p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-184" data-mp="3r495c2qo5exe">
            <BundleMockup compact data-id="element-185" data-mp="3m562moy5vksl" />
          </div>
          <p className="mt-6 text-sm" data-id="element-186" data-mp="3m6h60nnp2qj6">
            📧{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="break-all font-semibold underline" data-id="element-187" data-mp="1pcmfi4iijq2i">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p className="mt-2 text-sm" data-id="element-188" data-mp="1kb6vmfs2nplp">
            📲{' '}
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline" data-id="element-189" data-mp="2c6147n21do6o">
              {INSTAGRAM_HANDLE}
            </a>
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-xs leading-relaxed text-muted" data-id="element-190" data-mp="22h16s836n0ul">
            Disclaimer: this is a digital toolkit of coaching session guides and worksheets for professional coaches, for educational purposes. It is non-clinical and is
            not therapy, counseling, medical or psychological advice. It does not guarantee any result for you or your clients. Refer clients who need mental health
            support to a licensed professional.
          </p>
          <p className="mt-4 text-[11px] text-muted" data-id="element-191" data-mp="1eju0efdk3zvd">© {new Date().getFullYear()} SIGMA SMART™ · All rights reserved.</p>
        </div>
      </footer>

      {/* 12. MOBILE STICKY BAR (scrolls to the offer only) */}
      <StickyBar data-id="element-192" data-mp="38x3fcmovgdl0" />
    </div>;
}